/**
 * Browser Notifications Utility
 *
 * Handles browser notification permissions and display for real-time alerts.
 */

import { AlertSeverity, AlertType } from '../services/realtime-alert.service';
import type { Alert } from '../services/realtime-alert.service';
import { logger } from './logger';

// Check if browser supports notifications
export const supportsNotifications = (): boolean => {
  return 'Notification' in window;
};

// Get current permission status
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!supportsNotifications()) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (!supportsNotifications()) {
    console.warn('[Notifications] Browser does not support notifications');
    return 'unsupported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('[Notifications] Notification permission was denied');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    logger.info('[Notifications] Permission result:', permission);
    return permission;
  } catch (error) {
    console.error('[Notifications] Error requesting permission:', error);
    return 'default';
  }
};

// Get icon for alert type
const getAlertIcon = (alertType: AlertType): string => {
  const icons: Record<AlertType, string> = {
    [AlertType.SHELL_GAME_DETECTED]: '/icons/alert-shell-game.png',
    [AlertType.CRITICAL_VIOLATION]: '/icons/alert-violation.png',
    [AlertType.FATAL_CRASH]: '/icons/alert-fatal.png',
    [AlertType.OOS_ORDER_ISSUED]: '/icons/alert-oos.png',
    [AlertType.BASIC_THRESHOLD_CROSSED]: '/icons/alert-basic.png',
    [AlertType.CONTACT_MATCH_FOUND]: '/icons/alert-contact.png',
    [AlertType.RISK_SCORE_INCREASED]: '/icons/alert-risk.png',
    [AlertType.NEW_CARRIER_IN_NETWORK]: '/icons/alert-network.png',
    [AlertType.INSURANCE_EXPIRING]: '/icons/alert-insurance.png',
    [AlertType.AUTHORITY_REVOKED]: '/icons/alert-authority.png',
  };
  return icons[alertType] || '/icons/alert-default.png';
};

// Extended NotificationOptions with experimental properties
interface ExtendedNotificationOptions extends NotificationOptions {
  actions?: Array<{ action: string; title: string }>;
  vibrate?: number[];
  timestamp?: number;
}

// Get notification options based on severity
const getNotificationOptions = (alert: Alert): ExtendedNotificationOptions => {
  const baseOptions: ExtendedNotificationOptions = {
    body: alert.message,
    icon: getAlertIcon(alert.alertType),
    badge: '/icons/badge.png',
    tag: alert.id, // Prevents duplicate notifications for same alert
    data: {
      alertId: alert.id,
      dotNumber: alert.dotNumber,
      alertType: alert.alertType,
    },
    // Actions (if supported)
    actions: [
      { action: 'view', title: 'View' },
      { action: 'dismiss', title: 'Dismiss' },
    ],
  };

  // Critical alerts require interaction (won't auto-dismiss)
  if (alert.severity === AlertSeverity.CRITICAL) {
    baseOptions.requireInteraction = true;
    baseOptions.vibrate = [200, 100, 200, 100, 200]; // Vibration pattern
  }

  // Add timestamp
  baseOptions.timestamp = new Date(alert.createdAt).getTime();

  return baseOptions;
};

// Show browser notification for an alert
export const showBrowserNotification = (
  alert: Alert,
  onClick?: (alert: Alert) => void,
  onClose?: (alert: Alert) => void
): Notification | null => {
  if (!supportsNotifications()) {
    console.warn('[Notifications] Browser does not support notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('[Notifications] Permission not granted');
    return null;
  }

  try {
    const title = `[${alert.severity}] ${alert.title}`;
    const options = getNotificationOptions(alert);

    const notification = new Notification(title, options);

    // Handle click
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      notification.close();

      if (onClick) {
        onClick(alert);
      }
    };

    // Handle close
    notification.onclose = () => {
      if (onClose) {
        onClose(alert);
      }
    };

    // Handle error
    notification.onerror = (error) => {
      console.error('[Notifications] Error showing notification:', error);
    };

    logger.info('[Notifications] Notification shown:', alert.title);
    return notification;
  } catch (error) {
    console.error('[Notifications] Error creating notification:', error);
    return null;
  }
};

// Close a notification by alert ID
export const closeNotification = (alertId: string): void => {
  // Note: There's no built-in way to close notifications by tag
  // This would require keeping track of active notifications
  console.debug('[Notifications] closeNotification called for:', alertId);
};

// Test notification (for settings page)
export const showTestNotification = (): Notification | null => {
  const testAlert: Alert = {
    id: 'test-' + Date.now(),
    userId: 0,
    dotNumber: 123456,
    alertType: AlertType.SHELL_GAME_DETECTED,
    severity: AlertSeverity.HIGH,
    title: 'Test Alert',
    message: 'This is a test notification to verify your browser notification settings are working correctly.',
    data: { test: true },
    createdAt: new Date().toISOString(),
  };

  return showBrowserNotification(testAlert);
};

// Service worker registration for push notifications (future enhancement)
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Notifications] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    logger.info('[Notifications] Service worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('[Notifications] Service worker registration failed:', error);
    return null;
  }
};

// Check if should show notification based on document visibility
export const shouldShowNotification = (alert: Alert): boolean => {
  // Always show for critical alerts
  if (alert.severity === AlertSeverity.CRITICAL) {
    return true;
  }

  // Only show if page is not visible for non-critical alerts
  if (document.visibilityState === 'visible') {
    return false;
  }

  return true;
};

// Format alert for display
export const formatAlertTitle = (alert: Alert): string => {
  const severityEmoji: Record<AlertSeverity, string> = {
    [AlertSeverity.INFO]: '',
    [AlertSeverity.WARNING]: '',
    [AlertSeverity.HIGH]: '',
    [AlertSeverity.CRITICAL]: '',
  };

  return `${severityEmoji[alert.severity] || ''} ${alert.title}`.trim();
};

// Get severity color for UI
export const getSeverityColor = (severity: AlertSeverity): string => {
  const colors: Record<AlertSeverity, string> = {
    [AlertSeverity.INFO]: '#2196f3',
    [AlertSeverity.WARNING]: '#ff9800',
    [AlertSeverity.HIGH]: '#f57c00',
    [AlertSeverity.CRITICAL]: '#d32f2f',
  };
  return colors[severity] || '#666';
};

// Get severity background color for UI
export const getSeverityBgColor = (severity: AlertSeverity): string => {
  const colors: Record<AlertSeverity, string> = {
    [AlertSeverity.INFO]: '#e3f2fd',
    [AlertSeverity.WARNING]: '#fff3e0',
    [AlertSeverity.HIGH]: '#fff8e1',
    [AlertSeverity.CRITICAL]: '#ffebee',
  };
  return colors[severity] || '#f5f5f5';
};

// Format relative time
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  } else if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Format alert type for display
export const formatAlertType = (alertType: AlertType): string => {
  const labels: Record<AlertType, string> = {
    [AlertType.SHELL_GAME_DETECTED]: 'Shell Game',
    [AlertType.CRITICAL_VIOLATION]: 'Critical Violation',
    [AlertType.FATAL_CRASH]: 'Fatal Crash',
    [AlertType.OOS_ORDER_ISSUED]: 'Out of Service',
    [AlertType.BASIC_THRESHOLD_CROSSED]: 'BASIC Threshold',
    [AlertType.CONTACT_MATCH_FOUND]: 'Contact Match',
    [AlertType.RISK_SCORE_INCREASED]: 'Risk Score',
    [AlertType.NEW_CARRIER_IN_NETWORK]: 'New in Network',
    [AlertType.INSURANCE_EXPIRING]: 'Insurance Expiring',
    [AlertType.AUTHORITY_REVOKED]: 'Authority Revoked',
  };
  return labels[alertType] || alertType;
};
